import { useCallback } from 'react'

import emDash from '~/components/emdash'

import { type PercentUnit } from '../types/units'
import { useNumberFormatter } from './number'
import type { FormatterHook } from './types'
import { combineOptions } from './types'
import type { UnitsFormat } from './units'

const percentFormats: UnitsFormat<PercentUnit> = {
  percent: {
    long: ' Percent',
    short: '%'
  }
}

function isPercentUnit(unit: string): unit is PercentUnit {
  return unit in percentFormats
}

const defaultOptions = {
  longForm: false,
  sigFigs: 0,
  units: 'percent',
  omitUnits: false,
  invalidStr: emDash
}

const exportOptions = { ...defaultOptions, omitUnits: true, invalidStr: '', sigFigs: 2 }

// eslint-disable-next-line import/prefer-default-export
export const useSocFormatter: FormatterHook = () => {
  const formatNumber = useNumberFormatter()

  return useCallback(
    (value, options) => {
      const {
        value: soc,
        sigFigs,
        units,
        omitUnits,
        invalidStr
      } = combineOptions({
        value,
        options,
        defaults: defaultOptions,
        exports: exportOptions
      })

      if (!isPercentUnit(units)) return invalidStr
      if (typeof soc !== 'string' && typeof soc !== 'number') return invalidStr

      const socNum = parseFloat(`${soc}`)
      if (isNaN(socNum)) return invalidStr

      const formatOptions = {
        maximumFractionDigits: sigFigs,
        export: options?.export
      }

      if (omitUnits) {
        return formatNumber(socNum, formatOptions)
      } else {
        // The percent-style formatter renders 1 as 100%
        return formatNumber(socNum / 100, { ...formatOptions, style: 'percent' })
      }
    },
    [formatNumber]
  )
}
